import React, { useState, useEffect, useMemo } from "react";
import Context from "../../Context";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast, Spinner } from "../../components/ui";
import { Header } from "../../components/modules";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/base";
import { HiOutlineDocumentSearch } from "react-icons/hi";
import { BiLogIn } from "react-icons/bi";
import { FiSearch, FiRefreshCw } from "react-icons/fi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import constant, { statuses } from "../../constants";
import {
  RiCoupon2Line,
  RiFlashlightLine,
  RiAlarmLine,
  RiTimeLine,
  RiCheckDoubleLine,
  RiTicketLine,
} from "react-icons/ri";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/modules/alertDialog";

export const CouponOrders = ({ initialTab = "single", initialType = "" }) => {
  const { callApi, configs, db } = Context();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(initialTab);
  const [couponType, setCouponType] = useState(initialType);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roundDate, setRoundDate] = useState(configs.roundDate);
  const [pages, setPages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("shortId");
  const [status, setStatus] = useState("");

  // Sync state from location.pathname (e.g. /coupon/orders/voucher, /coupon/orders/bundle) or location.hash
  useEffect(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    const subRoute = parts.length >= 3 ? parts[2] : null;
    const hash = location.hash.replace("#", "");
    const target = subRoute || hash || "";

    if (target === "bundle") {
      setActiveTab("bundle");
      setCouponType("");
    } else if (target === "reserve" || target === "reserved") {
      setActiveTab("reserve");
      setCouponType("reserve");
    } else if (target === "discount") {
      setActiveTab("single");
      setCouponType("discount");
    } else if (target === "voucher") {
      setActiveTab("single");
      setCouponType("voucher");
    } else if (target === "vip") {
      setActiveTab("single");
      setCouponType("vip");
    } else if (target === "voucher-lock") {
      setActiveTab("single");
      setCouponType("voucher-lock");
    } else if (target === "mission") {
      setActiveTab("single");
      setCouponType("mission");
    } else {
      setActiveTab(initialTab);
      setCouponType(initialType);
    }
  }, [location.pathname, location.hash, initialTab, initialType]);

  useEffect(() => {
    setCurrentPage(1);
    getOrders();
    // eslint-disable-next-line
  }, [roundDate, status, activeTab, couponType]);

  const getOrders = async (customQueries = null) => {
    setLoading(true);
    try {
      const isBundle = activeTab === "bundle";
      const isReserve = activeTab === "reserve";
      const apiName = isBundle
        ? "coupons-getAllBundleOrders"
        : "coupons-getAllCouponOrders";

      const queries = customQueries !== null
        ? [...customQueries]
        : (searchValue ? [[searchType, searchValue]] : []);

      if (status) {
        queries.push(["status", status]);
      }
      if (isReserve) {
        queries.push(["coupon.type", "reserve"]);
      } else if (couponType) {
        if (couponType === "vip") {
          queries.push(["coupon.vip", true]);
        } else if (couponType === "mission") {
          queries.push(["isReward", true]);
        } else {
          queries.push(["coupon.type", couponType]);
        }
      }

      const result = await callApi(
        apiName,
        {
          roundDate,
          queries,
          limit: isReserve ? 2000 : 500,
          orderBy: isReserve ? ["coupon.lastUpdate", "desc"] : ["createdAt", "desc"],
        },
        { notShowLoading: true },
      );

      const chunkSize = 100;
      const paged = {};
      const list = result || [];
      for (let i = 0, page = 1; i < list.length; i += chunkSize, page++) {
        paged[page] = list.slice(i, i + chunkSize);
      }
      setPages(paged);
      setOrders(list);
    } catch (e) {
      setOrders([]);
      setPages({});
    }
    setLoading(false);
  };

  // Auto-Match & Morning Cron state
  const [autoMatchEnabled, setAutoMatchEnabled] = useState(false);
  const [updatingCron, setUpdatingCron] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isAutoMatching, setIsAutoMatching] = useState(false);
  const [autoMatchResult, setAutoMatchResult] = useState(null);
  const [isResultOpen, setIsResultOpen] = useState(false);
  const { toast } = useToast();

  // Fetch Config for morning cron toggle
  useEffect(() => {
    if (!db) return;
    const fetchConfig = async () => {
      try {
        const snap = await getDoc(doc(db, "Configs", "constant"));
        if (snap.exists()) {
          setAutoMatchEnabled(!!snap.get("autoMatchReserveEnabled"));
        }
      } catch (err) {
        console.warn("Failed to fetch autoMatchReserveEnabled:", err);
      }
    };
    fetchConfig();
  }, [db]);

  const handleToggleCron = async () => {
    if (!db || updatingCron) return;
    setUpdatingCron(true);
    const nextState = !autoMatchEnabled;
    try {
      await updateDoc(doc(db, "Configs", "constant"), {
        autoMatchReserveEnabled: nextState,
      });
      setAutoMatchEnabled(nextState);
      toast({
        title: nextState
          ? "เปิดระบบจัดสลากอัตโนมัติทุกเช้า 08:00 น. เรียบร้อย"
          : "ปิดระบบจัดสลากอัตโนมัติทุกเช้าเรียบร้อย",
        className: nextState ? "bg-green text-white" : "bg-gray-800 text-white",
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "เกิดข้อผิดพลาดในการบันทึกสถานะ",
        className: "bg-rose-600 text-white",
      });
    } finally {
      setUpdatingCron(false);
    }
  };

  const handleExecuteAutoMatch = async () => {
    setIsConfirmOpen(false);
    setIsAutoMatching(true);
    try {
      const res = await callApi("orders-autoMatchReserveOrders", {
        roundDate,
      });
      if (res) {
        setAutoMatchResult(res);
        setIsResultOpen(true);
        getOrders(); // Refresh table
      } else {
        toast({
          title: "ทำรายการไม่สำเร็จ หรือเกิดข้อผิดพลาด",
          className: "bg-rose-600 text-white",
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        title: "เกิดข้อผิดพลาดในการจัดสลากอัตโนมัติ",
        className: "bg-rose-600 text-white",
      });
    } finally {
      setIsAutoMatching(false);
    }
  };


  const sortedPage = useMemo(() => {
    const page = pages[currentPage] || [];
    if (activeTab === "reserve") {
      return [...page].sort(
        (a, b) =>
          (b.coupon?.reserveOrder?.filter((x) => x.status === "pending")?.length || 0) -
          (a.coupon?.reserveOrder?.filter((x) => x.status === "pending")?.length || 0)
      );
    }
    return [...page].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [pages, currentPage, activeTab]);

  const subHeaderTitle = useMemo(() => {
    if (activeTab === "bundle") return "คำสั่งซื้อชุดแพ็กเกจ";
    if (activeTab === "reserve" || couponType === "reserve") return "คำสั่งซื้อคูปองจองสิทธิ์";
    if (couponType === "voucher") return "คำสั่งซื้อคูปองสลาก";
    if (couponType === "discount") return "คำสั่งซื้อคูปองส่วนลด";
    if (couponType === "vip") return "คำสั่งซื้อคูปอง VIP";
    if (couponType === "voucher-lock") return "คำสั่งซื้อคูปองล็อคเลข";
    if (couponType === "mission") return "คำสั่งซื้อรางวัลภารกิจ";
    return "คำสั่งซื้อคูปอง";
  }, [activeTab, couponType]);

  // Statistics for reserve tab
  const reserveStats = useMemo(() => {
    if (activeTab !== "reserve" || !orders) return null;
    let totalPendingTickets = 0;
    let totalUsedTickets = 0;
    let totalPendingItems = 0;
    let totalOrders = orders.length;

    orders.forEach((order) => {
      const reserveOrders = order.coupon?.reserveOrder || [];
      reserveOrders.forEach((ro) => {
        const qty = Number(ro.quantity) || 1;
        if (ro.status === "pending" || !ro.status) {
          totalPendingTickets += qty;
          totalPendingItems++;
        } else if (ro.status === "used") {
          totalUsedTickets += qty;
        }
      });
    });

    return {
      totalOrders,
      totalPendingTickets,
      totalUsedTickets,
      totalPendingItems,
      totalAllocated: totalPendingTickets + totalUsedTickets,
    };
  }, [activeTab, orders]);

  return (
    <div className="h-fit w-full max-w-[1600px] mx-auto pb-10 min-w-0">
      <Header
        header="Coupons"
        subHeader={subHeaderTitle}
        icon={<RiCoupon2Line className="text-blue" />}
      />

      {/* Tabs */}
      <div className="flex gap-2 mt-4">
        <button
          type="button"
          onClick={() => {
            setActiveTab("single");
            setCouponType("");
            setStatus("");
            navigate("/coupon/orders");
          }}
          className={`py-2 px-4 text-sm font-bold rounded-md transition-all cursor-pointer ${
            activeTab === "single"
              ? "bg-blue text-white shadow-sm"
              : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
          }`}
        >
          คูปองเดี่ยว
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveTab("bundle");
            setCouponType("");
            setStatus("");
            navigate("/coupon/orders/bundle");
          }}
          className={`py-2 px-4 text-sm font-bold rounded-md transition-all cursor-pointer ${
            activeTab === "bundle"
              ? "bg-blue text-white shadow-sm"
              : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
          }`}
        >
          ชุดแพ็กเกจ
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveTab("reserve");
            setCouponType("reserve");
            setStatus("");
            navigate("/coupon/orders/reserve");
          }}
          className={`py-2 px-4 text-sm font-bold rounded-md transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === "reserve"
              ? "bg-blue text-white shadow-sm"
              : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
          }`}
        >
          <span>คูปองจองสิทธิ์</span>
          {reserveStats && reserveStats.totalPendingItems > 0 && (
            <span className="bg-amber-400 text-slate-900 text-[11px] font-extrabold px-1.5 py-0.2 rounded-full">
              {reserveStats.totalPendingItems}
            </span>
          )}
        </button>
      </div>

      {/* Reserve Tab: Modern 4 Mini KPI Cards */}
      {activeTab === "reserve" && reserveStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <div className="bg-white p-3.5 rounded-md border border-slate-200 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500">คูปองจองสิทธิ์ทั้งหมด</p>
              <h3 className="text-xl font-bold text-slate-900 mt-1">
                {reserveStats.totalOrders.toLocaleString()}{" "}
                <span className="text-xs font-normal text-slate-400">คำสั่งซื้อ</span>
              </h3>
            </div>
            <div className="w-10 h-10 rounded-md bg-blue/10 text-blue flex items-center justify-center">
              <RiCoupon2Line className="text-xl" />
            </div>
          </div>

          <div className="bg-white p-3.5 rounded-md border border-amber-200 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-amber-700">สิทธิ์รอดำเนินการ (Pending)</p>
              <h3 className="text-xl font-bold text-amber-600 mt-1">
                {reserveStats.totalPendingTickets.toLocaleString()}{" "}
                <span className="text-xs font-normal text-slate-400">ใบ</span>
              </h3>
              <p className="text-[11px] text-amber-600 font-medium">({reserveStats.totalPendingItems} รายการค้าง)</p>
            </div>
            <div className="w-10 h-10 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center">
              <RiTimeLine className="text-xl" />
            </div>
          </div>

          <div className="bg-white p-3.5 rounded-md border border-emerald-200 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-emerald-700">จัดสร้างออเดอร์แล้ว</p>
              <h3 className="text-xl font-bold text-emerald-600 mt-1">
                {reserveStats.totalUsedTickets.toLocaleString()}{" "}
                <span className="text-xs font-normal text-slate-400">ใบ</span>
              </h3>
              <p className="text-[11px] text-emerald-600 font-medium">
                {reserveStats.totalAllocated > 0
                  ? `${((reserveStats.totalUsedTickets / reserveStats.totalAllocated) * 100).toFixed(0)}% สำเร็จ`
                  : "-"}
              </p>
            </div>
            <div className="w-10 h-10 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <RiCheckDoubleLine className="text-xl" />
            </div>
          </div>

          <div className="bg-white p-3.5 rounded-md border border-slate-200 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500">รวมสิทธิ์ในงวดนี้</p>
              <h3 className="text-xl font-bold text-slate-900 mt-1">
                {reserveStats.totalAllocated.toLocaleString()}{" "}
                <span className="text-xs font-normal text-slate-400">ใบ</span>
              </h3>
              <p className="text-[11px] text-slate-400 font-medium">งวด {roundDate}</p>
            </div>
            <div className="w-10 h-10 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center">
              <RiTicketLine className="text-xl" />
            </div>
          </div>
        </div>
      )}

      {/* Filters & Actions Bar */}
      <div className="mt-3 bg-white rounded-md shadow-xs border border-gray-200 p-4 flex flex-col gap-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setCurrentPage(1);
            getOrders(searchValue ? [[searchType, searchValue]] : []);
          }}
          className="flex flex-wrap items-end gap-3 shrink-0"
        >
          <div>
            <label className="text-xs font-bold text-gray-700">งวดสลาก</label>
            <div className="relative mt-1">
              <select
                className="h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-hidden focus:border-blue transition-all cursor-pointer appearance-none px-3"
                onChange={(e) => setRoundDate(e.target.value)}
                value={roundDate || ""}
              >
                {configs.roundDates?.map((date, idx) => (
                  <option key={idx} value={date}>
                    {date}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-gray-700">สถานะ</label>
            <div className="relative mt-1">
              <select
                disabled={loading}
                className="h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-hidden focus:border-blue transition-all cursor-pointer appearance-none disabled:text-gray-400 px-3"
                value={status || ""}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">ทั้งหมด</option>
                <option value={constant.statuses.APPROVE}>
                  {constant.statusesTranslate.approved}
                </option>
                <option value={constant.statuses.PAID}>
                  {constant.statusesTranslate.paid}
                </option>
                <option value={constant.statuses.PENDING}>
                  {constant.statusesTranslate.pending}
                </option>
                <option value={constant.statuses.CANCEL}>
                  {constant.statusesTranslate.cancel}
                </option>
              </select>
            </div>
          </div>

          {activeTab !== "bundle" && (
            <div>
              <label className="text-xs font-bold text-gray-700">ประเภทคูปอง</label>
              <div className="relative mt-1">
                <select
                  disabled={loading}
                  className="h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-hidden focus:border-blue transition-all cursor-pointer appearance-none disabled:text-gray-400 px-3"
                  value={couponType}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCouponType(val);
                    if (val === "reserve") {
                      setActiveTab("reserve");
                      navigate("/coupon/orders/reserve");
                    } else if (val) {
                      setActiveTab("single");
                      navigate(`/coupon/orders/${val}`);
                    } else {
                      setActiveTab("single");
                      navigate("/coupon/orders");
                    }
                  }}
                >
                  <option value="">ทั้งหมด</option>
                  <option value="voucher">คูปองสลาก (Voucher)</option>
                  <option value="discount">คูปองส่วนลด (Discount)</option>
                  <option value="voucher-lock">คูปองสลากล็อคเลข</option>
                  <option value="reserve">คูปองจองสิทธิ์ (Reserve)</option>
                  <option value="vip">คูปอง VIP</option>
                  <option value="mission">รางวัลภารกิจ (Mission)</option>
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-bold text-gray-700">ค้นหาตาม</label>
            <div className="relative mt-1">
              <select
                disabled={loading}
                className="h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-hidden focus:border-blue transition-all cursor-pointer appearance-none disabled:text-gray-400 px-3"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
              >
                <option value="shortId">รหัสคำสั่งซื้อ</option>
                <option value="userData.phone">เบอร์โทรศัพท์</option>
                <option value="userData.userId">รหัสผู้ใช้</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-gray-700">คำค้นหา</label>
            <div className="relative flex items-center mt-1">
              <input
                disabled={loading}
                className="h-10 w-full bg-white border border-gray-300 text-sm font-semibold rounded-md shadow-xs focus:outline-hidden focus:border-blue transition-all placeholder:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed px-3"
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="พิมพ์เพื่อค้นหา..."
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={loading}
              className="h-10 px-4 flex items-center justify-center gap-1.5 bg-blue hover:bg-blue/90 text-white text-sm font-bold rounded-md shadow-xs transition-all duration-150 disabled:opacity-50 cursor-pointer"
            >
              <FiSearch className="w-4 h-4" />
              <span>ค้นหา</span>
            </button>
            <button
              type="button"
              disabled={loading}
              className="h-10 px-4 flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-md border border-gray-300 shadow-xs transition-all cursor-pointer disabled:opacity-50"
              onClick={() => {
                setSearchValue("");
                setStatus("");
                setCouponType("");
                setCurrentPage(1);
                navigate("/coupon/orders");
                getOrders([]);
              }}
            >
              <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              <span>รีเซ็ต</span>
            </button>
          </div>
        </form>

        {/* Reserve Tab: Premium Action Hero Banner */}
        {activeTab === "reserve" && (
          <div className="pt-3.5 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4 bg-gradient-to-r from-emerald-50/70 via-teal-50/40 to-transparent p-3 rounded-md border border-emerald-100/80">
            {/* Auto-Match Trigger Button */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={loading || isAutoMatching}
                onClick={() => setIsConfirmOpen(true)}
                className="h-10 px-4.5 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-sm font-bold rounded-md shadow-sm hover:shadow-md transition-all cursor-pointer disabled:opacity-50"
              >
                {isAutoMatching ? (
                  <>
                    <Spinner className="w-4 h-4 text-white" />
                    <span>กำลังจัดสลากอัตโนมัติ...</span>
                  </>
                ) : (
                  <>
                    <RiFlashlightLine className="w-5 h-5 text-amber-300 animate-pulse" />
                    <span>
                      ⚡ จัดสลากอัตโนมัติทั้งหมด
                      {reserveStats && reserveStats.totalPendingItems > 0 && (
                        <span className="ml-1.5 px-2 py-0.5 rounded-full bg-white/25 text-white text-xs">
                          {reserveStats.totalPendingItems} ค้าง
                        </span>
                      )}
                    </span>
                  </>
                )}
              </button>
              <p className="text-xs text-slate-600 font-medium leading-relaxed">
                กวาดสลากว่างจากคลังหลังบ้าน (และบนแผงที่ไม่ติดจอง) นำมาสร้างออเดอร์ตามคิวเวลาจองให้อัตโนมัติ
              </p>
            </div>

            {/* Morning 08:00 Cron Switch Widget */}
            <div className="flex items-center gap-3 bg-white border border-slate-200 px-3.5 py-2 rounded-md shadow-xs">
              <div className="flex items-center gap-2 text-slate-700">
                <RiAlarmLine className={`w-4 h-4 ${autoMatchEnabled ? "text-emerald-600" : "text-slate-400"}`} />
                <span className="text-xs font-bold text-slate-700">รันอัตโนมัติทุกเช้า 08:00 น. :</span>
              </div>
              <button
                type="button"
                disabled={updatingCron}
                onClick={handleToggleCron}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden ${
                  autoMatchEnabled ? "bg-emerald-600" : "bg-slate-300"
                } disabled:opacity-50`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out ${
                    autoMatchEnabled ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
              <span className={`text-xs font-bold ${autoMatchEnabled ? "text-emerald-700" : "text-slate-500"}`}>
                {autoMatchEnabled ? "เปิดใช้งาน" : "ปิด"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Orders Table */}
      <div className="mt-4 bg-white rounded-md shadow-md border border-gray-200 overflow-hidden">
        <div className="flex items-center p-4 border-b border-gray-200 bg-gray-50/60 justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-800">{subHeaderTitle}</h2>
            <span className="px-2.5 py-0.5 text-xs font-bold bg-gray-200 text-gray-700 rounded-md">
              {orders ? orders.length : 0} รายการ
            </span>
          </div>
          <div>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    className={`${
                      currentPage > 1
                        ? "cursor-pointer"
                        : "cursor-not-allowed opacity-45"
                    } font-semibold`}
                    onClick={() =>
                      setCurrentPage((prev) => (prev > 1 ? prev - 1 : 1))
                    }
                  />
                </PaginationItem>
                {Object.keys(pages).map((pageNumber) => (
                  <PaginationItem key={pageNumber}>
                    <PaginationLink
                      className={`${
                        currentPage === Number(pageNumber)
                          ? "bg-blue text-white font-semibold"
                          : ""
                      } rounded-md cursor-pointer`}
                      onClick={() => setCurrentPage(Number(pageNumber))}
                    >
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    className={`${
                      currentPage < Object.keys(pages).length
                        ? "cursor-pointer"
                        : "cursor-not-allowed opacity-45"
                    } font-semibold`}
                    onClick={() =>
                      currentPage < Object.keys(pages).length &&
                      setCurrentPage((prev) => prev + 1)
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>

        <div className="w-full overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-100/90 border-b border-gray-200">
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  ลำดับ
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  วันที่-เวลา
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  สถานะ
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  รหัสคำสั่งซื้อ
                </TableHead>
                <TableHead className="font-bold text-gray-700 py-3 whitespace-nowrap">
                  {activeTab === "bundle" ? "ชื่อแพ็กเกจ" : "คูปอง"}
                </TableHead>
                <TableHead className="font-bold text-gray-700 py-3 whitespace-nowrap">
                  ชื่อ-นามสกุล
                </TableHead>
                <TableHead className="font-bold text-gray-700 py-3 whitespace-nowrap">
                  เบอร์โทรศัพท์
                </TableHead>
                <TableHead className="text-right font-bold text-gray-700 py-3 whitespace-nowrap">
                  ราคา
                </TableHead>
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  สลิป
                </TableHead>
                {activeTab !== "bundle" && (
                  <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                    {activeTab === "reserve" ? "สิทธิ์จอง" : "สถานะคูปอง"}
                  </TableHead>
                )}
                <TableHead className="text-center font-bold text-gray-700 py-3 whitespace-nowrap">
                  จัดการ
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="h-[80px]">
                  <TableCell colSpan={activeTab !== "bundle" ? 11 : 10}>
                    <div className="w-full h-72 flex flex-col justify-center items-center gap-3 text-gray-500">
                      <Spinner className="w-8 h-8" />
                      <p className="text-sm font-semibold">
                        กำลังโหลดข้อมูล{subHeaderTitle}...
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow className="h-[80px]">
                  <TableCell
                    colSpan={activeTab !== "bundle" ? 11 : 10}
                    className="h-72 font-semibold text-center text-gray-400"
                  >
                    ไม่พบข้อมูล
                  </TableCell>
                </TableRow>
              ) : (
                sortedPage.map((item, index) => (
                  <OrderCell
                    key={item.id}
                    order={item}
                    index={index}
                    activeTab={activeTab}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Confirmation Modal */}
      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-emerald-600 font-bold text-lg">
              <RiFlashlightLine className="w-6 h-6" />
              <span>ยืนยันการจัดสลากอัตโนมัติ (Auto-Match)</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 text-sm mt-2 space-y-2">
              <p>
                ระบบจะทำการกวาดค้นหาสิทธิ์จองสลากที่ค้างอยู่ (Pending) ทั้งหมดในงวด <strong className="text-blue">{roundDate}</strong>
              </p>
              <ul className="list-disc pl-5 text-xs space-y-1 text-gray-500">
                <li>จัดสรรตามลำดับเวลาที่ลูกค้ากดจองก่อน-หลัง (FIFO)</li>
                <li>ดึงสลากว่างจากคลังหลังบ้านก่อน หากไม่พอจะดึงจากแผง (ที่ไม่ติดตะกร้าลูกค้า) ให้อัตโนมัติ</li>
                <li>สร้างออเดอร์และตัดสิทธิ์จองให้ทันที</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel
              disabled={isAutoMatching}
              onClick={() => setIsConfirmOpen(false)}
              className="border-gray-300 text-gray-700"
            >
              ยกเลิก
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isAutoMatching}
              onClick={handleExecuteAutoMatch}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
            >
              ยืนยันและเริ่มจัดสลาก
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Auto-Match Result Summary Modal */}
      <AlertDialog open={isResultOpen} onOpenChange={setIsResultOpen}>
        <AlertDialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-gray-800 font-bold text-lg">
              <RiFlashlightLine className="w-5 h-5 text-emerald-600" />
              <span>ผลการจัดสลากอัตโนมัติ (Auto-Match)</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 text-sm">
              งวดสลากประจำวันที่: <strong className="text-blue">{roundDate}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {autoMatchResult && (
            <div className="mt-3 flex-1 overflow-y-auto space-y-4 pr-1">
              {/* Stat Cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                  <div className="text-xs text-emerald-700 font-semibold">สร้างคำสั่งซื้อสำเร็จ</div>
                  <div className="text-2xl font-bold text-emerald-700 mt-1">
                    {autoMatchResult.totalTicketsMatched || 0}{" "}
                    <span className="text-sm font-normal">ใบ</span>
                  </div>
                  <div className="text-[11px] text-emerald-600">
                    ({autoMatchResult.totalSuccessOrders || 0} ออเดอร์)
                  </div>
                </div>

                <div className="p-3 bg-rose-50 border border-rose-200 rounded-md">
                  <div className="text-xs text-rose-700 font-semibold">สร้างไม่สำเร็จ / สลากไม่พอ</div>
                  <div className="text-2xl font-bold text-rose-700 mt-1">
                    {autoMatchResult.totalFailedTickets || 0}{" "}
                    <span className="text-sm font-normal">ใบ</span>
                  </div>
                  <div className="text-[11px] text-rose-600">
                    ({autoMatchResult.totalFailed || 0} รายการคงค้าง)
                  </div>
                </div>
              </div>

              {/* Failed items list if any */}
              {autoMatchResult.failedList && autoMatchResult.failedList.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-rose-700 mb-1.5 flex items-center gap-1">
                    <span>⚠️ รายการที่สลากไม่พอหรือติดจอง ({autoMatchResult.failedList.length} รายการ):</span>
                  </h4>
                  <div className="max-h-40 overflow-y-auto border border-rose-200 rounded-md divide-y divide-rose-100 bg-rose-50/40 text-xs">
                    {autoMatchResult.failedList.map((fail, idx) => (
                      <div key={idx} className="p-2 flex justify-between items-center">
                        <div>
                          <div className="font-bold text-gray-800">
                            เลขท้าย {fail.number} ({fail.quantity} ใบ)
                          </div>
                          <div className="text-gray-500 text-[11px]">
                            {fail.customerName}
                          </div>
                        </div>
                        <span className="text-[11px] text-rose-600 font-medium">
                          {fail.reason}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <AlertDialogFooter className="mt-4 border-t pt-3">
            <AlertDialogAction
              onClick={() => setIsResultOpen(false)}
              className="bg-blue hover:bg-blue/90 text-white font-bold w-full sm:w-auto"
            >
              รับทราบ / ปิดหน้าต่าง
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const OrderCell = ({ order, index, activeTab }) => {
  const { callApi } = Context();
  const [loadingUserToken, setLoadingUserToken] = useState(false);
  const isBundle = activeTab === "bundle";
  const isReserve = activeTab === "reserve";

  const itemName = isBundle ? order.bundle?.name : order.coupon?.name;
  const itemPrice = isBundle ? order.price : order.coupon?.price;
  const detailUrl = isBundle ? `/coupon/bundle/${order.id}` : `/coupon/${order.id}`;

  return (
    <TableRow className="h-[80px]">
      <TableCell className="align-baseline text-center font-semibold">
        {index + 1}
      </TableCell>
      <TableCell className="align-baseline font-semibold whitespace-nowrap text-center">
        <div>
          {new Date(order.createdAt).toLocaleDateString("th-TH", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
          })}{" "}
          น.
        </div>
      </TableCell>
      <TableCell className="font-semibold align-baseline">
        <div
          className={`${
            order.status === statuses.APPROVE
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : order.status === statuses.PAID
              ? "bg-sky-50 text-sky-700 border border-sky-200"
              : order.status === statuses.PENDING
              ? "bg-amber-50 text-amber-800 border border-amber-200"
              : "bg-rose-50 text-rose-700 border border-rose-200"
          } min-w-16 w-fit px-2.5 py-1 text-xs font-bold text-center rounded-md mx-auto shadow-2xs`}
        >
          {constant.statusesTranslate[order.status] || order.status}
        </div>
      </TableCell>
      <TableCell className="align-baseline font-bold text-center font-mono text-base tabular-nums">
        {order.shortId}
      </TableCell>
      <TableCell className="align-baseline font-semibold">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span>{itemName}</span>
            {(order.isReward || order.missionOrder) && (
              <span className="bg-purple-100 text-purple-700 border border-purple-300 text-[11px] font-bold px-1.5 py-0.5 rounded-md">
                รางวัลภารกิจ
              </span>
            )}
          </div>
          {order.missionOrder && (
            <span
              onClick={() =>
                window.open(`/mission/${order.missionOrder}`, "_blank")
              }
              className="text-xs text-purple-600 hover:text-purple-800 hover:underline cursor-pointer flex items-center gap-1 font-mono"
            >
              ภารกิจ: #{String(order.missionOrder).slice(-6).toUpperCase()}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="font-semibold align-baseline">
        {order.userData?.name}
      </TableCell>
      <TableCell className="font-semibold align-baseline">
        {order.userData?.phone}
      </TableCell>
      <TableCell className="font-semibold text-right align-baseline">
        {(order.isReward || order.missionOrder) ? (
          <div>
            <span className="text-purple-700 font-bold">ฟรี (รางวัลภารกิจ)</span>
            {itemPrice > 0 && (
              <p className="text-xs text-gray-400 line-through">
                {Number(itemPrice || 0).toLocaleString()} บาท
              </p>
            )}
          </div>
        ) : !isBundle && order.coupon?.isPointRedeem ? (
          <div>
            <p>{Number(order.price || 0).toLocaleString()}</p>
            <p className="text-xs text-gray-500">
              คะแนนหลังหัก: {Number(order.pointAfterCharge || 0).toLocaleString()} คะแนน
            </p>
          </div>
        ) : (
          <p>{Number(itemPrice || 0).toLocaleString()} บาท</p>
        )}
      </TableCell>
      <TableCell className="font-semibold text-center">
        {(order.isReward || order.missionOrder) && !order.slip ? (
          <span className="text-xs text-purple-600 font-medium">รางวัลภารกิจ</span>
        ) : !isBundle && order.coupon?.isPointRedeem ? (
          "แลกด้วยคะแนน"
        ) : order.slip ? (
          <img
            onClick={() => window.open(order.slip, "_blank")}
            src={order.slip}
            className="mx-auto w-16 max-h-20 object-contain cursor-pointer rounded-sm"
            alt={order.slipId || "slip"}
          />
        ) : (
          "-"
        )}
      </TableCell>

      {!isBundle && (
        <TableCell className="font-semibold align-baseline">
          {isReserve || order.coupon?.type === "reserve" ? (
            <div className="space-y-1.5 max-w-[220px]">
              <div className="flex items-center justify-between text-[11px] font-semibold text-slate-500 mb-1">
                <span>ความคืบหน้าสิทธิ์:</span>
                <span className="font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded-md">
                  {`${order.coupon?.reserveOrder?.length || 0}/${order.coupon?.quantity || 0}`}
                </span>
              </div>
              {order.coupon?.reserveOrder?.map((resOrder, i) => {
                const isUsed = resOrder?.status === "used";
                const isPending = resOrder?.status === "pending" || !resOrder?.status;
                return (
                  <div
                    key={i}
                    onClick={() =>
                      resOrder.orderId &&
                      window.open(`/order/${resOrder.orderId}`, "_blank")
                    }
                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-md border text-xs font-semibold transition-all ${
                      isUsed
                        ? "bg-emerald-50/80 text-emerald-800 border-emerald-200 hover:bg-emerald-100 cursor-pointer shadow-2xs"
                        : isPending
                        ? "bg-amber-50 text-amber-900 border-amber-200"
                        : "bg-slate-50 text-slate-600 border-slate-200"
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      {isUsed ? (
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                      ) : (
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0 animate-pulse" />
                      )}
                      <span className="font-mono font-bold">
                        เลข {resOrder.number}
                      </span>
                      <span className="text-[11px] text-slate-500 font-normal">
                        ({resOrder.quantity} ใบ)
                      </span>
                    </div>

                    <div className="text-[11px] font-bold">
                      {isUsed ? (
                        <span className="text-emerald-700 hover:underline">
                          #{resOrder.shortId || resOrder.orderId?.slice(-5).toUpperCase()}
                        </span>
                      ) : isPending ? (
                        <span className="text-amber-700">รอจัด</span>
                      ) : (
                        constant.statusesTranslate[resOrder?.status] || resOrder?.status
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              className={`${
                order.coupon?.status === statuses.ACTIVE
                  ? "bg-green"
                  : order.coupon?.status === statuses.USED
                  ? "bg-gray-600"
                  : order.coupon?.status === statuses.INACTIVE
                  ? "bg-amber-500"
                  : "bg-rose-600"
              } min-w-14 w-fit px-3 py-1.5 text-white text-xs font-bold text-center rounded-md mx-auto`}
            >
              {constant.statusesTranslate[order.coupon?.status] || order.coupon?.status}
            </div>
          )}
        </TableCell>
      )}

      <TableCell className="font-semibold align-baseline">
        <div className="flex justify-center items-center gap-1.5">
          {!isBundle && (
            <button
              title="เข้าสู่ระบบในชื่อผู้ใช้"
              className="flex items-center justify-center w-8 h-8 bg-sky-50 text-sky-700 border border-sky-200 rounded-md shadow-2xs hover:bg-sky-100 transition-all cursor-pointer disabled:opacity-50"
              onClick={async () => {
                setLoadingUserToken(true);
                const result = await callApi("users-addAdminToken", {
                  userId: order.userId,
                });
                if (result?.adminToken) {
                  window.open(constant.getAdminOrderUrl(result.adminToken));
                }
                setLoadingUserToken(false);
              }}
            >
              {loadingUserToken ? (
                <Spinner className="w-3.5 h-3.5 text-sky-700" />
              ) : (
                <BiLogIn className="w-4 h-4" />
              )}
            </button>
          )}
          <button
            title="ดูรายละเอียดคำสั่งซื้อ"
            className="flex items-center justify-center w-8 h-8 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md shadow-2xs hover:bg-emerald-100 transition-all cursor-pointer"
            onClick={() => window.open(detailUrl, "_blank")}
          >
            <HiOutlineDocumentSearch className="w-4 h-4" />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
};
